import { useState } from 'react';
import Select from 'react-select';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CONDITION_OPTIONS = [
    { value: 'is', label: 'is' },
    { value: 'is_not', label: 'is not' }
];

const OS_OPTIONS = [
    { value: 'Windows', label: 'Windows' },
    { value: 'macOS', label: 'macOS' },
    { value: 'Linux', label: 'Linux' },
    
    { value: 'Android', label: 'Android' },
    { value: 'Chrome OS', label: 'Chrome OS' },
    { value: 'Other', label: 'Other' }
];

const OSFilterModal = ({ isOpen, onClose, onApply }) => {
    const [condition, setCondition] = useState('is');
    const [selectedOS, setSelectedOS] = useState([]);

    const handleApply = () => {
        if (!selectedOS.length) {
            onClose();
            return;
        }

        // Join multi values with |
        const values = selectedOS.map(o => o.value).join('|');
        const fParams = [`${condition},os,${values}`];

        onApply(fParams);
        onClose();
    };

    const handleReset = () => {
        setCondition('is');
        setSelectedOS([]);
    };

    if (!isOpen) return null;

    return (
        <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={modalStyle}>
                {/* Header */}
                <div style={headerStyle}>
                    <h3 style={titleStyle}>Filter by Operating System</h3>
                    <button onClick={onClose} style={closeBtnStyle}>
                        <X size={18} />
                    </button>
                </div>

                {/* OS filter */}
                <div style={{ marginBottom: '20px' }}>
                    <label style={labelStyle}>Operating System</label>

                    <div style={{ display: 'flex', gap: '10px' }}>
                        {/* Condition */}
                        <select
                            value={condition}
                            onChange={e => setCondition(e.target.value)}
                            style={selectStyle}
                        >
                            {CONDITION_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>
                                    {o.label}
                                </option>
                            ))}
                        </select>

                        {/* Multi-select */}
                        <div style={{ flex: 1 }}>
                            <Select
                                options={OS_OPTIONS}
                                value={selectedOS}
                                onChange={val => setSelectedOS(val || [])}
                                isMulti
                                isClearable
                                placeholder="Select operating systems"
                                styles={reactSelectStyles}
                            />
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={footerStyle}>
                    <Button
                        variant="outline"
                        onClick={handleReset}
                        style={{ marginRight: '10px' }}
                    >
                        Reset
                    </Button>
                    <Button
                        onClick={handleApply}
                        disabled={!selectedOS.length}
                        style={{ backgroundColor: '#6366f1', color: '#fff' }}
                    >
                        Apply filter
                    </Button>
                </div>
            </div>
        </div>
    );
};

/* Styles */
const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.45)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999
};

const modalStyle = {
    width: '460px',
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12), 0 2px 8px rgba(15, 23, 42, 0.04)'
};

const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
};

const titleStyle = {
    margin: 0,
    fontSize: '16px',
    fontWeight: 600,
    color: '#0f172a',
    letterSpacing: '-0.01em'
};

const footerStyle = {
    marginTop: '20px',
    paddingTop: '16px',
    borderTop: '1px solid #f1f5f9',
    display: 'flex',
    justifyContent: 'flex-end'
};

const closeBtnStyle = {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#64748b',
    width: 28,
    height: 28,
    borderRadius: 9999,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.15s, color 0.15s'
};

const labelStyle = {
    fontSize: '12px',
    fontWeight: 500,
    color: '#64748b',
    marginBottom: '8px',
    display: 'block'
};

const selectStyle = {
    padding: '10px 12px',
    borderRadius: '12px',
    border: '1px solid #e2e8f0',
    fontSize: '14px',
    background: '#fff',
    color: '#0f172a',
    minWidth: '120px',
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
};

const reactSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: 40,
        fontSize: 14,
        borderRadius: 12,
        borderColor: state.isFocused ? '#6366f1' : '#e2e8f0',
        boxShadow: state.isFocused
            ? '0 0 0 3px rgba(99, 102, 241, 0.12)'
            : '0 1px 2px rgba(15, 23, 42, 0.04)',
        '&:hover': { borderColor: state.isFocused ? '#6366f1' : '#cbd5e1' },
        transition: 'box-shadow 0.2s, border-color 0.2s'
    }),
    placeholder: (base) => ({ ...base, color: '#94a3b8' }),
    singleValue: (base) => ({ ...base, color: '#0f172a' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base) => ({ ...base, color: '#94a3b8', padding: 6 }),
    menu: (base) => ({
        ...base,
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.1)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
    }),
    option: (base, state) => ({
        ...base,
        fontSize: 13,
        color: '#0f172a',
        backgroundColor: state.isSelected ? '#eef2ff' : state.isFocused ? '#f8fafc' : 'transparent',
        cursor: 'pointer'
    }),
    multiValue: (base) => ({
        ...base,
        backgroundColor: '#eef2ff',
        borderRadius: 9999,
        paddingLeft: 4,
        paddingRight: 2
    }),
    multiValueLabel: (base) => ({
        ...base,
        color: '#4338ca',
        fontSize: 12,
        fontWeight: 500
    }),
    multiValueRemove: (base) => ({
        ...base,
        color: '#4338ca',
        borderRadius: 9999,
        ':hover': { backgroundColor: '#c7d2fe', color: '#312e81' }
    })
};

export default OSFilterModal;
